import React, { useCallback, useEffect, useMemo, useState } from 'react';
import i18next from 'i18next';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

const useStyles = makeStyles(() => ({
  container: {
    whiteSpace: 'nowrap'
  },

  button: {
    fontWeight: 'bold',
    textTransform: 'capitalize'
  }
}));

const FALLBACK_LANGUAGE_CODE = 'en-US';

/**
 * @see https://github.com/i18next/i18next-browser-languageDetector
 */
const LOCAL_STORAGE_LANGUAGE_CODE_KEY = 'i18nextLng';

// FIXME: (jh) Refactor this somewhere else?
const { get: getLanguageCode, set: setCachedLanguageCode } = {
  get: () =>
    window.localStorage?.getItem(LOCAL_STORAGE_LANGUAGE_CODE_KEY) ||
    i18next.language ||
    FALLBACK_LANGUAGE_CODE,
  set: defaultLanguageCode =>
    window.localStorage?.setItem(
      LOCAL_STORAGE_LANGUAGE_CODE_KEY,
      defaultLanguageCode
    )
};

export default function LanguageSelector({
  languageCodes = ['en-US', 'fr-CA', 'es'],
  className,
  ...rest
}) {
  const classes = useStyles();

  const DEFAULT_LANGUAGE_CODE = useMemo(() => getLanguageCode(), []);

  const [selectedLanguageCode, _setSelectedLanguageCode] = useState(
    DEFAULT_LANGUAGE_CODE
  );

  /**
   * Shaves off any language code variants (i.e. en-US) and uses the main
   * version (i.e. "en").
   *
   * @param {string} languageCode
   * @return {string} Altered version without "-variant" suffix
   */
  const getNormalizedLanguageCode = useCallback(
    languageCode => languageCode.split('-')[0],
    []
  );

  const handleSetSelectedLanguageCode = useCallback(languageCode => {
    _setSelectedLanguageCode(languageCode);
  }, []);

  const { i18n } = useTranslation();

  // Automatically change language based on selected language code
  useEffect(() => {
    if (selectedLanguageCode !== i18next.language) {
      i18n.changeLanguage(selectedLanguageCode);
    }

    setCachedLanguageCode(selectedLanguageCode);

    // Reload the page if the language changes
    //
    // NOTE: This fixes an issue where certain page elements (i.e. left
    // sidebar) didn't get dynamically translated
    if (DEFAULT_LANGUAGE_CODE !== selectedLanguageCode) {
      window.location.reload();
    }
  }, [i18n, selectedLanguageCode, DEFAULT_LANGUAGE_CODE]);

  return (
    <div {...rest} className={clsx(classes.container, className)}>
      {languageCodes.map((languageCode, idx) => (
        <React.Fragment key={languageCode}>
          <button
            className={classes.button}
            onClick={() => handleSetSelectedLanguageCode(languageCode)}
            disabled={selectedLanguageCode === languageCode}
          >
            {getNormalizedLanguageCode(languageCode)}
          </button>

          {idx < languageCodes.length - 1 && <span> | </span>}
        </React.Fragment>
      ))}
    </div>
  );
}

LanguageSelector.propTypes = {
  languageCodes: PropTypes.object,
  className: PropTypes.string
};
